import { Button } from '@/components/atoms/NewPcButton';
import { useEffect, useState } from 'react';
import PcMembershipHeroBg from '@/assets/images/pc-membership/hero-bg.png';

import useCheckMembership from '@/hooks/useCheckMembership';
import { useMembershipPaymentResult } from '@/hooks/useMembershipPaymentResult';
import { useAppSelector } from '@/redux/store';
import { Dialog, DialogContent } from '@/components/atoms/Dialog';

import NewPaymentDialog from '@/containers/payment/NewPaymentDialog';
import { CPLAT_API_ROUTES, usePost } from '@/api';

import MembershipFailDialog from '@/containers/payment/MembershipFailDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/V3/useToast';
import AlertDialog from '@/components/dashboard/alert-dialog';
import type { MembershipPaymentResult } from '@/utils/membership-payment-result';
import { ROUTES } from '@/routes/paths';
import { useMobileMembershipPolicy } from '@/pages/mobile-membership/use-mobile-membership-policy';
import {
  PC_HERO_BADGE_EARLY_BIRD_TYPE,
  PC_HERO_BADGE_FIRST_PAYMENT_TYPE,
  PC_HERO_BADGE_STANDARD_TYPE,
  type MobileMembershipPolicy,
  type PcHeroBadge,
  type PcHeroBadgeType,
  getIsInAppToWebAvailable,
} from '@/pages/mobile-membership/mobile-membership-utils';
import { useSidebar } from '@/components/atoms/Sidebar';
import { cn } from '@/utils/tailwind';
import {
  MembershipFeatureSection,
  MembershipNoticeSection,
  MembershipSupportSection,
} from '@/pages/mobile-membership/mobile-membership-sections';
import { useQueryClient } from '@tanstack/react-query';
import { WEB_PC_MEMBERSHIP_CHANNEL } from '@/utils/pending-subscription';
import { AlertCircle } from 'lucide-react';

type PaymentCompletionEvent = {
  result: 'success' | 'fail';
  paymentChange: boolean;
  paymentResultId: string | null;
};

const PcMembership = () => {
  const isMember = useCheckMembership();
  const navigate = useNavigate();
  const { isMobile, state: sidebarState } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAppSelector((state) => state.user);
  const [billDialogOpen, setBillDialogOpen] = useState<boolean>(false);
  const [appConversionDialogOpen, setAppConversionDialogOpen] = useState<boolean>(false);
  const [isFailOpen, setIsFailOpen] = useState<boolean>(false);
  const [paymentCompletion, setPaymentCompletion] = useState<PaymentCompletionEvent | null>(null);
  const searchParamString = searchParams.toString();
  const { policy, isPolicyLoading, isPolicyError } = useMobileMembershipPolicy('WEB_PC', {
    enableMock: false,
  });
  const queryClient = useQueryClient();

  const { showToast } = useToast();

  const { mutate: getUserInfo } = usePost(CPLAT_API_ROUTES.getUserInfo);
  const {
    paymentResult,
    isFetching: isPaymentResultFetching,
    isLoading: isPaymentResultLoading,
  } = useMembershipPaymentResult({
    cplatToken: user.CplatToken,
    paymentResultId: paymentCompletion?.paymentResultId,
    channel: 'WEB_PC',
    enabled: paymentCompletion?.result === 'success',
  });

  useEffect(() => {
    const nextSearchParams = new URLSearchParams(searchParamString);
    const result = nextSearchParams.get('result');
    const paymentChange = nextSearchParams.get('paymentChange') === 'true';
    const paymentResultId = nextSearchParams.get('paymentResultId');
    const pendingSubscription = nextSearchParams.get('pendingSubscription');

    if (pendingSubscription === 'success' || pendingSubscription === 'fail') {
      showToast(
        pendingSubscription === 'success' ? 'success' : 'warning',
        pendingSubscription === 'success'
          ? '웹 멤버십 전환 예약이 완료되었어요.'
          : '웹 멤버십 전환 예약 신청에 실패했습니다. 다시 시도해 주세요.',
      );
      nextSearchParams.delete('pendingSubscription');
      setSearchParams(nextSearchParams, { replace: true });
      return;
    }

    if (result !== 'success' && result !== 'fail') return;

    setPaymentCompletion({
      result,
      paymentChange,
      paymentResultId,
    });

    nextSearchParams.delete('result');
    nextSearchParams.delete('paymentChange');
    nextSearchParams.delete('paymentResultId');
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParamString, setSearchParams, showToast]);

  useEffect(() => {
    if (!paymentCompletion) return;

    if (paymentCompletion.result === 'fail') {
      setIsFailOpen(true);
      setPaymentCompletion(null);
      return;
    }

    if (paymentCompletion.paymentResultId && (isPaymentResultLoading || isPaymentResultFetching)) {
      return;
    }

    getUserInfo({ cplatToken: user.CplatToken });
    showToast(
      'congratulations',
      getMembershipSuccessToastText(paymentCompletion.paymentChange, paymentResult),
    );
    setPaymentCompletion(null);
  }, [
    getUserInfo,
    isPaymentResultFetching,
    isPaymentResultLoading,
    paymentCompletion,
    paymentResult,
    showToast,
    user.CplatToken,
  ]);

  // 다이얼로그 오픈 시 getUserInfo 호출
  useEffect(() => {
    if (isFailOpen) {
      getUserInfo({ cplatToken: user.CplatToken });
    }
  }, [isFailOpen, getUserInfo, user.CplatToken]);

  const { subscriptionNo, isPrePayment, paymentAmount } = policy;
  const isInAppToWebAvailable = getIsInAppToWebAvailable(policy);
  const canRenderPaymentDialog = !isPolicyLoading && !isPolicyError;
  const shouldShowAppConversionDialog =
    canRenderPaymentDialog && isInAppToWebAvailable && getIsPcAppConversionDialogVisibleInKst();
  const shouldShowSubscribedButton = canRenderPaymentDialog && isMember && !isInAppToWebAvailable;
  const shouldShowRegularAmount = getShouldShowPcRegularAmount(policy, canRenderPaymentDialog);
  const heroLeftOffset = isMobile ? 0 : sidebarState === 'collapsed' ? 55 : 220;
  const openPaymentDialog = () => {
    if (!canRenderPaymentDialog) return;

    setBillDialogOpen(true);
  };
  const openAppConversionDialog = () => {
    if (!canRenderPaymentDialog) return;

    setAppConversionDialogOpen(true);
  };
  const handleAppConversionButtonClick = shouldShowAppConversionDialog
    ? openAppConversionDialog
    : openPaymentDialog;
  const handleAppConversionDialogCtaClick = () => {
    setAppConversionDialogOpen(false);
    navigate(ROUTES.mobileMembership);
  };
  const handlePendingSubscriptionSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: [CPLAT_API_ROUTES.getMembershipQuote] });
    getUserInfo({ cplatToken: user.CplatToken });
    showToast('success', '웹 멤버십 전환 예약이 완료되었어요.');
  };

  useEffect(() => {
    if (!shouldShowAppConversionDialog) return;

    setAppConversionDialogOpen(true);
  }, [shouldShowAppConversionDialog]);

  if (isPolicyError) {
    return <PcMembershipPolicyError />;
  }

  return (
    <div className="scrollbar relative flex h-full w-full flex-1 flex-col overflow-y-auto bg-gr-50">
      <AlertDialog />
      <section
        className="fixed right-0 top-[66px] z-[1] h-[362px] min-w-0 overflow-hidden bg-[#0f111b] transition-[left] duration-200"
        style={{ left: heroLeftOffset }}
      >
        <img
          src={PcMembershipHeroBg}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-bk/20" aria-hidden="true" />
        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1200px] items-center justify-between gap-6 px-8 max-[1180px]:gap-4 max-[1180px]:px-6 min-[1500px]:px-0">
          <div className="relative flex w-[clamp(240px,28vw,281px)] flex-col items-start gap-[10px] max-[1180px]:w-[min(300px,45vw)]">
            <div
              className="pointer-events-none absolute left-[-92px] top-1/2 h-[360px] w-[420px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(130,118,252,0.22)_0%,rgba(130,118,252,0.12)_36%,rgba(130,118,252,0)_72%)] blur-[18px]"
              aria-hidden="true"
            />
            <p className="relative z-10 w-full whitespace-nowrap tracking-[5.74px] text-point-400/80 Body4S14 max-[1180px]:text-[12px] max-[1180px]:leading-[18px] max-[1180px]:tracking-[3px]">
              SELLERBOX MEMBERSHIP
            </p>
            <h1 className="relative z-10 whitespace-pre-line text-[clamp(30px,3vw,38px)] font-semibold leading-[1.2] text-wh max-[1180px]:text-[30px]">
              온라인 셀러를 위한{'\n'}올인원 관리 솔루션
            </h1>
            <p className="relative z-10 text-point-400/80 Body5M14">
              모든 마켓을 한 곳에서 관리하세요
            </p>
          </div>

          <div className="relative flex w-[clamp(300px,29vw,360px)] shrink-0 flex-col gap-[clamp(14px,1.4vw,18px)] overflow-hidden rounded-[20px] border border-wh/20 bg-wh/10 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(255,255,255,0.08),0_18px_52px_rgba(0,0,0,0.24)] backdrop-blur-[10px] max-[1180px]:w-[240px] max-[1180px]:gap-3 max-[1180px]:p-4">
            <div
              className="pointer-events-none absolute inset-0 rounded-[20px] bg-[linear-gradient(135deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.04)_42%,rgba(130,118,252,0.1)_100%)]"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute -bottom-16 left-12 h-[180px] w-[180px] rounded-full bg-point-500/30 blur-[38px]"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute -right-8 top-10 h-[150px] w-[150px] rounded-full bg-point-400/20 blur-[34px]"
              aria-hidden="true"
            />
            <div
              className="bg-wh/35 pointer-events-none absolute inset-x-0 top-0 h-px"
              aria-hidden="true"
            />
            <PcHeroBadge badge={policy.pcHeroBadge} />
            <div className="relative z-10 flex flex-col gap-1">
              {shouldShowRegularAmount && (
                <p className="text-[clamp(12px,1.1vw,14px)] font-medium leading-[1.57] text-wh/70 line-through max-[1180px]:text-[12px]">
                  {policy.paymentSummary.regularAmountLabel}/월
                </p>
              )}
              <div className="flex items-end gap-[6px] text-wh">
                <strong className="text-[clamp(32px,3vw,38px)] font-semibold leading-[1.2] max-[1180px]:text-[28px]">
                  {isPolicyLoading ? '정책 확인 중' : policy.paymentSummary.currentPaymentLabel}
                </strong>
                {!isPolicyLoading && (
                  <span className="pb-1 text-[clamp(14px,1.3vw,16px)] font-medium leading-[1.5] max-[1180px]:text-[12px]">
                    /월
                  </span>
                )}
              </div>
              <p className="break-keep text-[clamp(12px,1.1vw,14px)] font-medium leading-[1.57] text-wh/70 max-[1180px]:text-[12px]">
                {isPolicyLoading
                  ? '멤버십 정보를 불러오고 있어요'
                  : getPcMembershipHeroPriceDescription(policy)}
              </p>
            </div>
            {shouldShowSubscribedButton ? (
              <Button
                className="relative z-10 h-11 w-full rounded-[8px] bg-point-500 text-[clamp(12px,1.1vw,14px)] font-medium leading-[1.57] text-white disabled:bg-point-500 disabled:text-white max-[1180px]:h-9"
                disabled
              >
                구독중
              </Button>
            ) : (
              <Button
                className="relative z-10 h-11 w-full rounded-[8px] bg-point-500 text-[clamp(12px,1.1vw,14px)] font-medium leading-[1.57] text-white max-[1180px]:h-9"
                onClick={isInAppToWebAvailable ? handleAppConversionButtonClick : openPaymentDialog}
                disabled={!canRenderPaymentDialog}
              >
                {isPolicyLoading
                  ? '정책 확인 중'
                  : isInAppToWebAvailable
                  ? '월 100원으로 다시 구독하기'
                  : '지금 시작하기'}
              </Button>
            )}
            <p className="relative z-10 text-center text-[clamp(11px,1vw,12px)] font-medium leading-[1.5] text-wh/70">
              언제든 해지 가능
            </p>
          </div>
        </div>
      </section>

      <div className="h-[362px] shrink-0" aria-hidden="true" />
      <div className="relative z-20 flex w-full flex-1 flex-col bg-gr-50 px-[clamp(20px,5vw,68px)] py-10 shadow-[0_-18px_42px_rgba(0,0,0,0.12)]">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8">
          <MembershipFeatureSection variant="pc" />
          <MembershipSupportSection variant="pc" />
          <MembershipNoticeSection variant="pc" />
        </div>
      </div>

      <MembershipFailDialog open={isFailOpen} setOpen={setIsFailOpen} />
      {shouldShowAppConversionDialog && (
        <Dialog open={appConversionDialogOpen} onOpenChange={setAppConversionDialogOpen}>
          <PcAppConversionDialog onCtaClick={handleAppConversionDialogCtaClick} />
        </Dialog>
      )}
      {canRenderPaymentDialog && (
        <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
          <NewPaymentDialog
            isPrePayment={isInAppToWebAvailable ? true : isPrePayment}
            subscriptionNo={subscriptionNo}
            paymentAmount={paymentAmount}
            setBillDialogOpen={setBillDialogOpen}
            mode={isInAppToWebAvailable ? 'pendingSubscription' : 'subscription'}
            pendingSubscriptionChannel={WEB_PC_MEMBERSHIP_CHANNEL}
            onPendingSubscriptionSuccess={handlePendingSubscriptionSuccess}
          />
        </Dialog>
      )}
    </div>
  );
};

export default PcMembership;

const PC_HERO_BADGE_MIN_WIDTH_CLASS_BY_TYPE: Record<PcHeroBadgeType, string> = {
  [PC_HERO_BADGE_EARLY_BIRD_TYPE]: 'min-w-[93px]',
  [PC_HERO_BADGE_STANDARD_TYPE]: 'min-w-[69px]',
  [PC_HERO_BADGE_FIRST_PAYMENT_TYPE]: 'min-w-[60px]',
};

const PC_APP_CONVERSION_DIALOG_VISIBLE_MONTHS = new Set([5, 6]);
const KST_MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  timeZone: 'Asia/Seoul',
});

const getIsPcAppConversionDialogVisibleInKst = (date = new Date()) => {
  return PC_APP_CONVERSION_DIALOG_VISIBLE_MONTHS.has(Number(KST_MONTH_FORMATTER.format(date)));
};

type PcHeroBadgeProps = {
  badge: PcHeroBadge;
};

const PcHeroBadge = ({ badge }: PcHeroBadgeProps) => {
  if (!badge.enabled || badge.label.trim().length === 0) return null;

  return (
    <div
      className={cn(
        'relative z-10 flex h-[34px] w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-[8px] bg-point-100 px-[10px] text-point-500 Body5M14',
        PC_HERO_BADGE_MIN_WIDTH_CLASS_BY_TYPE[badge.type],
      )}
    >
      {badge.label}
    </div>
  );
};

type PcAppConversionDialogProps = {
  onCtaClick: () => void;
};

const PcAppConversionDialog = ({ onCtaClick }: PcAppConversionDialogProps) => {
  return (
    <DialogContent
      noCloseBtn
      className="w-[560px] max-w-[calc(100vw-56px)] overflow-visible rounded-[32px] !border-0 !bg-transparent p-0 !shadow-none"
    >
      <section className="relative min-h-[266px] overflow-hidden rounded-[32px] bg-[#0f111b] shadow-[0_20px_64px_rgba(0,0,0,0.26)]">
        <img
          src={PcMembershipHeroBg}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center opacity-70"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(90deg,rgba(15,17,27,0.98)_0%,rgba(15,17,27,0.86)_42%,rgba(15,17,27,0.55)_100%)]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -left-10 -top-20 h-[260px] w-[300px] rounded-full bg-point-500/20 blur-[50px]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -right-20 bottom-0 h-[220px] w-[260px] rounded-full bg-point-400/20 blur-[48px]"
          aria-hidden="true"
        />
        <div className="relative z-10 flex min-h-[266px] flex-col px-8 py-8">
          <div className="flex flex-col items-start gap-[10px]">
            <p className="whitespace-nowrap tracking-[5px] text-point-400/80 Body4S14">
              SELLERBOX MEMBERSHIP
            </p>
            <h2 className="whitespace-pre-line text-[26px] font-semibold leading-[38px] text-wh">
              앱에서 멤버십 해지 후 웹에서 결제하시면{'\n'}3개월간 월 100원으로 이용하실 수 있어요
            </h2>
            <p className="whitespace-pre-line text-point-400/80 Body5M14">
              혜택은 셀러박스 웹에서 가입하신 경우에만 적용되며,{'\n'}인앱 결제는 혜택 대상에서
              제외됩니다.
            </p>
          </div>
          <Button
            type="button"
            className="mt-5 h-10 w-full rounded-[8px] bg-point-500 text-wh Body5M14"
            onClick={onCtaClick}
          >
            웹에서 결제하고 100원 혜택 받기
          </Button>
        </div>
      </section>
    </DialogContent>
  );
};

const getPcMembershipHeroPriceDescription = (policy: MobileMembershipPolicy) => {
  const promotionPriceDescription = policy.promotion.priceDescription.trim();

  if (promotionPriceDescription.length > 0) {
    return promotionPriceDescription;
  }

  return 'VAT 포함 · 자동 결제';
};

const getShouldShowPcRegularAmount = (
  policy: MobileMembershipPolicy,
  canUsePolicyAmount: boolean,
) => {
  const { paymentAmount, regularAmount } = policy;

  return canUsePolicyAmount && paymentAmount < regularAmount;
};

const PcMembershipPolicyError = () => {
  return (
    <div className="flex h-full w-full flex-1 items-center justify-center bg-gr-50 px-6">
      <section className="flex max-w-[420px] flex-col items-center text-center">
        <div className="size-14 flex items-center justify-center rounded-full bg-gr-100 text-gr-500">
          <AlertCircle className="size-7" aria-hidden="true" />
        </div>
        <h1 className="mt-6 text-gr-900 Title3S18">멤버십 정보를 불러오지 못했어요</h1>
        <p className="mt-2 break-keep text-gr-600 Body5M14">
          결제 정책을 확인할 수 없어 결제를 진행할 수 없습니다. 잠시 후 다시 시도해 주세요.
        </p>
        <Button
          type="button"
          className="mt-6 h-11 rounded-sm px-6 Body5M14"
          onClick={() => window.location.reload()}
        >
          다시 시도
        </Button>
      </section>
    </div>
  );
};

const getMembershipSuccessToastText = (
  paymentChange: boolean,
  paymentResult?: MembershipPaymentResult,
) => {
  if (paymentResult?.status === 'PENDING') {
    return '결제는 완료되었고 멤버십 정보를 반영 중입니다.';
  }

  if (paymentChange) {
    return paymentResult?.paymentMethodLabel
      ? `멤버십 결제 수단 변경이 완료되었어요! (${paymentResult.paymentMethodLabel})`
      : '멤버십 결제 수단 변경이 완료되었어요!';
  }

  return paymentResult?.paymentAmountLabel
    ? `멤버십 구독이 완료되었어요! 결제 금액 ${paymentResult.paymentAmountLabel}`
    : '멤버십 구독이 완료되었어요! 지금 바로 셀러박스 플러스 기능을 이용해 보세요.';
};
